import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Pencil, Check, X, Plus } from "lucide-react";

export default function SendersTab() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [editEmail, setEditEmail] = useState("");
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");

  const { data: senders = [], isLoading } = useQuery({
    queryKey: ["/api/email-mgmt/triggers/senders"],
    queryFn: () => apiRequest("GET", "/api/email-mgmt/triggers/senders") as unknown as Promise<any[]>,
  });

  const startEdit = (s: any) => {
    setEditing(s.key);
    setEditEmail(s.email);
    setEditName(s.displayName);
  };

  const save = async (key: string) => {
    setSaving(true);
    try {
      await apiRequest("PUT", `/api/email-mgmt/triggers/senders/${key}`, { email: editEmail, displayName: editName });
      showSuccess("Sender updated");
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ["/api/email-mgmt/triggers/senders"] });
    } catch { showError("Failed to save"); }
    setSaving(false);
  };

  const addSender = async () => {
    if (!newKey.trim() || !newEmail.includes('@')) return showError("Valid key and email required");
    setSaving(true);
    try {
      await apiRequest("PUT", `/api/email-mgmt/triggers/senders/${newKey.trim().toLowerCase()}`, { email: newEmail, displayName: newName });
      showSuccess("Sender added");
      setNewKey(""); setNewEmail(""); setNewName(""); setAdding(false);
      queryClient.invalidateQueries({ queryKey: ["/api/email-mgmt/triggers/senders"] });
    } catch { showError("Failed to add"); }
    setSaving(false);
  };

  if (isLoading) return <div className="text-muted-foreground text-sm">Loading...</div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Configure the "From" address for each email type. The display name appears in the recipient's inbox (e.g. <span className="font-mono text-xs">ChefAIQ Support &lt;support@chefaiq.com&gt;</span>).
        Make sure all addresses are verified in <a href="https://console.aws.amazon.com/ses" target="_blank" className="text-primary underline">AWS SES</a>.
      </p>

      <div className="space-y-3">
        {senders.map((s: any) => (
          <div key={s.key} className="border rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium capitalize">{s.key}</span>
                  {s.isOverridden && <Badge variant="secondary" className="text-xs">DB override</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mb-2">{s.usedFor}</p>
                {editing === s.key ? (
                  <div className="space-y-2">
                    <div className="flex gap-2 flex-wrap items-center">
                      <Input placeholder="Display Name" value={editName} onChange={e => setEditName(e.target.value)} className="h-8 text-sm w-44" />
                      <Input type="email" placeholder="email@chefaiq.com" value={editEmail} onChange={e => setEditEmail(e.target.value)} className="h-8 text-sm w-52" />
                      <Button size="icon" className="h-8 w-8" disabled={saving} onClick={() => save(s.key)}><Check className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(null)}><X className="h-4 w-4" /></Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Preview: <span className="font-mono">{editName ? `${editName} <${editEmail}>` : editEmail}</span></p>
                  </div>
                ) : (
                  <span className="text-sm font-mono text-blue-600">{s.full || s.email}</span>
                )}
              </div>
              {editing !== s.key && (
                <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => startEdit(s)}><Pencil className="h-4 w-4" /></Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {adding ? (
        <div className="border rounded-lg p-4 border-dashed space-y-2">
          <p className="text-sm font-medium">Add New Sender</p>
          <div className="flex gap-2 flex-wrap items-center">
            <Input placeholder="Key (e.g. billing)" value={newKey} onChange={e => setNewKey(e.target.value)} className="h-8 text-sm w-32" />
            <Input placeholder="Display Name" value={newName} onChange={e => setNewName(e.target.value)} className="h-8 text-sm w-40" />
            <Input type="email" placeholder="email@chefaiq.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} className="h-8 text-sm w-52" />
            <Button size="sm" className="h-8" disabled={saving} onClick={addSender}><Check className="h-4 w-4 mr-1" />Save</Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setAdding(false)}><X className="h-4 w-4" /></Button>
          </div>
          {newName && newEmail && <p className="text-xs text-muted-foreground">Preview: <span className="font-mono">{newName} &lt;{newEmail}&gt;</span></p>}
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setAdding(true)} className="w-full">
          <Plus className="h-4 w-4 mr-2" />Add Sender
        </Button>
      )}
    </div>
  );
}
